import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  PartyPopper, ShoppingCart, Plus, Minus, ArrowRight, 
  Tent, Speaker, Utensils, Gift, Star, Phone, ChevronRight, MessageCircle
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  furniture: "🪑",
  shelter: "⛺",
  decorations: "🎈",
  audio: "🔊",
  serving: "🍽️",
  entertainment: "🎪"
};

const categoryLabels = {
  furniture: "Tables & Chairs",
  shelter: "Tents & Canopy",
  decorations: "Decorations",
  audio: "Sound & Audio",
  serving: "Serving Equipment",
  entertainment: "Entertainment"
};

// WhatsApp helper
const openWhatsApp = (message) => {
  const phone = "18762772648";
  const encodedMessage = encodeURIComponent(message);
  window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
};

const RentalsPage = () => {
  const [rentals, setRentals] = useState([]);
  const [ratings, setRatings] = useState({});
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, comment: "" });

  useEffect(() => {
    fetchRentals();
    // Load cart from localStorage
    const savedCart = localStorage.getItem("rentalCart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  const fetchRentals = async () => {
    try {
      const response = await axios.get(`${API}/rentals`);
      setRentals(response.data);
      // Fetch ratings for all rentals
      const ratingsData = {};
      for (const rental of response.data) {
        try {
          const ratingRes = await axios.get(`${API}/rentals/${rental.id}/rating`);
          ratingsData[rental.id] = ratingRes.data;
        } catch (e) {
          ratingsData[rental.id] = { average: 0, count: 0 };
        }
      }
      setRatings(ratingsData);
    } catch (error) {
      console.error("Error fetching rentals:", error);
      toast.error("Failed to load rental equipment");
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async (rentalId) => {
    if (!reviewForm.name || !reviewForm.comment) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      await axios.post(`${API}/rentals/${rentalId}/reviews`, {
        rental_id: rentalId,
        customer_name: reviewForm.name,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      });
      toast.success("Review submitted! Thank you!");
      setReviewDialog(null);
      setReviewForm({ name: "", rating: 5, comment: "" });
      // Refresh ratings
      const ratingRes = await axios.get(`${API}/rentals/${rentalId}/rating`);
      setRatings(prev => ({ ...prev, [rentalId]: ratingRes.data }));
    } catch (error) {
      toast.error("Failed to submit review");
    }
  };

  const addToCart = (rental) => {
    const existingItem = cart.find(item => item.rental_id === rental.id);
    let newCart;
    
    if (existingItem) {
      newCart = cart.map(item => 
        item.rental_id === rental.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      newCart = [...cart, {
        rental_id: rental.id,
        rental_name: rental.name,
        quantity: 1,
        price_per_day: rental.price_per_day,
        days: 1
      }];
    }
    
    setCart(newCart);
    localStorage.setItem("rentalCart", JSON.stringify(newCart));
    toast.success(`${rental.name} added to cart!`);
  };

  const updateQuantity = (rentalId, change) => {
    const newCart = cart.map(item => {
      if (item.rental_id === rentalId) {
        const newQuantity = item.quantity + change;
        if (newQuantity <= 0) return null;
        return { ...item, quantity: newQuantity };
      }
      return item;
    }).filter(Boolean);
    
    setCart(newCart);
    localStorage.setItem("rentalCart", JSON.stringify(newCart));
  };

  const updateDays = (rentalId, days) => {
    const newCart = cart.map(item => 
      item.rental_id === rentalId ? { ...item, days: Math.max(1, days) } : item
    );
    setCart(newCart);
    localStorage.setItem("rentalCart", JSON.stringify(newCart));
  };

  const removeFromCart = (rentalId) => {
    const newCart = cart.filter(item => item.rental_id !== rentalId);
    setCart(newCart);
    localStorage.setItem("rentalCart", JSON.stringify(newCart));
    toast.info("Item removed from cart");
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price_per_day * item.quantity * item.days), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const categories = [...new Set(rentals.map(r => r.category))];
  
  const filteredRentals = selectedCategory === "all" 
    ? rentals 
    : rentals.filter(r => r.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <PartyPopper className="w-10 h-10 text-primary" />
            <span className="font-heading text-2xl font-semibold text-foreground">Party Rentals</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/rental-packages">
              <Button variant="outline" className="bg-yellow-100 border-yellow-400 text-yellow-800 hover:bg-yellow-200">
                🎁 Package Deals
              </Button>
            </Link>
            <Link to="/gallery">
              <Button variant="outline" className="bg-pink-100 border-pink-400 text-pink-800 hover:bg-pink-200">
                📸 Gallery
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
            <Link to="/order">
              <Button className="relative bg-primary">
                <ShoppingCart className="w-5 h-5 mr-2" />
                Order Now
                {getCartItemCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center">
                    {getCartItemCount()}
                  </span>
                )}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-2xl md:text-3xl font-bold text-foreground mb-2">Sis Carol</p>
          <div className="inline-flex items-center gap-2 bg-white/80 rounded-full px-4 py-2 shadow-sm mb-6">
            <Phone className="w-4 h-4 text-primary" />
            <span className="text-primary font-medium">1876-277-2648</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-4">
            Party Rental Equipment 🎉
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Everything you need for the perfect party! Tables, chairs, tents, decorations, sound systems, and more. Rent by the day.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {Object.entries(categoryLabels).map(([key, label]) => (
              <Badge key={key} variant="outline" className="text-sm py-2 px-4 bg-white">
                {categoryIcons[key]} {label}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 bg-white border-b sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              onClick={() => setSelectedCategory("all")}
              className="rounded-full"
            >
              All Items
            </Button>
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? "default" : "outline"}
                onClick={() => setSelectedCategory(cat)}
                className="rounded-full"
              >
                {categoryIcons[cat]} {categoryLabels[cat]}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Rentals Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading equipment...</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredRentals.map((rental) => {
                const cartItem = cart.find(item => item.rental_id === rental.id);
                return (
                  <Card key={rental.id} className={`overflow-hidden hover:shadow-lg transition-shadow ${rental.available === false ? 'opacity-60' : ''}`} data-testid={`rental-${rental.id}`}>
                    <div className="h-40 overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100">
                      {rental.image_url ? (
                        <img 
                          src={rental.image_url} 
                          alt={rental.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-6xl">{categoryIcons[rental.category]}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-heading text-lg font-semibold">{rental.name}</h3>
                        <Badge variant="outline" className="text-xs">
                          {categoryLabels[rental.category]}
                        </Badge>
                      </div>
                      {/* Star Rating */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star} 
                              className={`w-4 h-4 ${star <= (ratings[rental.id]?.average || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          ({ratings[rental.id]?.count || 0} reviews)
                        </span>
                        <button 
                          onClick={() => setReviewDialog(rental)}
                          className="text-xs text-primary hover:underline"
                        >
                          Write review
                        </button>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{rental.description}</p>
                      {rental.available === false && (
                        <Badge className="mb-2 bg-red-100 text-red-800">Currently Unavailable</Badge>
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-heading text-2xl text-primary">J${rental.price_per_day.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">per day</p>
                        </div>
                        {rental.available !== false && (
                          cartItem ? (
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="outline" onClick={() => updateQuantity(rental.id, -1)}>
                                <Minus className="w-4 h-4" />
                              </Button>
                              <span className="font-bold w-8 text-center">{cartItem.quantity}</span>
                              <Button size="sm" variant="outline" onClick={() => updateQuantity(rental.id, 1)}>
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button onClick={() => addToCart(rental)} className="bg-primary">
                              <Plus className="w-4 h-4 mr-1" /> Add
                            </Button>
                          )
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Floating Cart Summary */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <ShoppingCart className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-semibold">{getCartItemCount()} item(s) in cart</p>
                  <p className="text-sm text-muted-foreground">Rental Total: <span className="font-heading text-primary text-lg">J${getCartTotal().toLocaleString()}</span>/day</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => {setCart([]); localStorage.removeItem("rentalCart");}}>
                  Clear Cart
                </Button>
                <Link to="/order">
                  <Button className="bg-primary">
                    Continue to Order <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Cart Items Preview */}
            <div className="mt-4 flex flex-wrap gap-2">
              {cart.map(item => (
                <Badge key={item.rental_id} variant="secondary" className="py-1 px-3">
                  {item.rental_name} x{item.quantity}
                  <button 
                    onClick={() => removeFromCart(item.rental_id)}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Need Cakes Too? 🎂
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Combine your rental order with delicious cakes from Sis Carol!
          </p>
          <Link to="/order">
            <Button className="bg-white text-purple-600 hover:bg-purple-50 rounded-full px-8 py-6 text-lg font-bold">
              Order Cakes & Rentals <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white/70">
            © 2026 Sis Carol Cakes & Party Rentals | Irie Eatz Restaurant, Market Street, Ocho Rios
          </p>
          <p className="text-white/70 mt-2">
            <Phone className="w-4 h-4 inline mr-1" /> 1876-277-2648
          </p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button
        onClick={() => openWhatsApp("Hi Sis Carol! I'm interested in renting party equipment. Can you help me?")}
        className="fixed bottom-24 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
        data-testid="whatsapp-float-btn"
        title="Chat on WhatsApp"
      >
        <MessageCircle className="w-7 h-7" />
      </button>

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Write a Review for {reviewDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Your Name</label>
              <Input 
                placeholder="Enter your name"
                value={reviewForm.name}
                onChange={(e) => setReviewForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Rating</label>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                    className="p-1"
                  >
                    <Star 
                      className={`w-8 h-8 ${star <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Your Review</label>
              <Textarea 
                placeholder="Share your experience..."
                value={reviewForm.comment}
                onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                rows={3}
              />
            </div>
            <Button 
              className="w-full bg-primary"
              onClick={() => submitReview(reviewDialog?.id)}
            >
              Submit Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentalsPage;
